import React, { useEffect } from 'react';
import Modal from 'react-modal';
import { X } from 'lucide-react';

const ViewBookingModal = ({ isOpen, onClose, booking }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!booking) return null;

  // Calculate number of nights and total price with safeguards
  let nights = 0;
  let totalPrice = 0;
  
  try {
    const checkIn = new Date(booking.check_in_date);
    const checkOut = new Date(booking.check_out_date);
    if (!isNaN(checkIn.getTime()) && !isNaN(checkOut.getTime())) {
      nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
      totalPrice = parseFloat(booking.total_price) || 0;
    }
  } catch (e) {
    console.error('Error calculating booking details:', e);
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed':
        return 'text-green-700 bg-green-50 border-green-400';
      case 'Pending':
        return 'text-yellow-700 bg-yellow-50 border-yellow-400';
      case 'Cancelled':
        return 'text-red-700 bg-red-50 border-red-400';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-400';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="bg-white rounded-lg shadow-2xl w-11/12 sm:w-full max-w-4xl p-4 sm:p-6 max-h-[85vh] sm:max-h-[90vh] overflow-y-auto z-50 outline-none"
      overlayClassName="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-40 p-4 overflow-hidden"
      ariaHideApp={false}
    >
      <div className="space-y-2 sm:space-y-3">
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between border-b border-gray-300 pb-2 sm:pb-3 gap-2">
          <h2 className="text-lg sm:text-xl font-bold text-prose-brand break-words">Booking #{booking.id}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition duration-200 flex-shrink-0"
          >
            <X size={20} className="text-prose-black/60" />
          </button>
        </div>

        {/* Status Badge */}
        <span className={`inline-block px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-semibold border ${getStatusColor(booking.status)}`}>
          {booking.status}
        </span>

        {/* Property Info */}
        <div className="bg-gray-50 rounded-lg border border-gray-300 p-2 sm:p-3">
          <p className="text-xs sm:text-sm font-semibold text-prose-brand mb-1 sm:mb-2">Property</p>
          <div className="space-y-0.5 sm:space-y-1 text-xs sm:text-sm">
            <div className="flex justify-between gap-2 flex-wrap">
              <span className="text-prose-black/60">Name:</span>
              <span className="text-prose-brand font-semibold break-words text-right">{booking.property?.title}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-prose-black/60">ID:</span>
              <span className="text-prose-black font-mono">#{booking.property_id}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-prose-black/60">Per Night:</span>
              <span className="text-prose-gold-dark font-semibold">£{(booking.total_price / nights).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="bg-gray-50 rounded-lg border border-gray-300 p-2 sm:p-3">
          <p className="text-xs sm:text-sm font-semibold text-prose-brand mb-1 sm:mb-2">Customer</p>
          <div className="space-y-0.5 sm:space-y-1 text-xs sm:text-sm">
            <p className="text-prose-black font-semibold break-words">{booking.customer?.name}</p>
            <p className="text-prose-black truncate">
              <span className="text-prose-black/60">📧 </span>{booking.customer?.email}
            </p>
            <p className="text-prose-black">
              <span className="text-prose-black/60">📱 </span>{booking.customer?.phone}
            </p>
            <p className="text-prose-black">
              <span className="text-prose-black/60">📍 </span>{booking.customer?.address}
            </p>
          </div>
        </div>

        {/* Dates */}
        <div className="bg-gray-50 rounded-lg border border-gray-300 p-2 sm:p-3">
          <p className="text-xs sm:text-sm font-semibold text-prose-brand mb-1 sm:mb-2">Dates</p>
          <div className="space-y-0.5 sm:space-y-1 text-xs sm:text-sm">
            <div className="flex justify-between gap-2">
              <span className="text-prose-black/60">Check-in:</span>
              <span className="text-prose-black font-semibold">
                {new Date(booking.check_in_date).toLocaleDateString('en-GB', {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-prose-black/60">Check-out:</span>
              <span className="text-prose-black font-semibold">
                {new Date(booking.check_out_date).toLocaleDateString('en-GB', {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-prose-gold/10 rounded-lg border border-prose-gold/30 p-2 sm:p-3">
          <div className="space-y-0.5 sm:space-y-1">
            <div className="flex justify-between text-xs sm:text-sm gap-2">
              <span className="text-prose-black/60">Nights:</span>
              <span className="font-semibold">{nights}</span>
            </div>
            <div className="flex justify-between text-sm sm:text-base font-bold border-t border-prose-gold/30 pt-1 sm:pt-2 gap-2">
              <span className="text-prose-brand">Total:</span>
              <span className="text-prose-gold-dark">£{totalPrice.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full px-3 sm:px-4 py-2 bg-prose-brand text-white rounded-lg hover:bg-prose-brand/90 transition duration-200 font-semibold text-sm sm:text-base"
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

export default ViewBookingModal;
